/**
 * Notification store for managing in-app notifications.
 *
 * Features:
 * - Unread count badge
 * - Notification list with infinite scroll
 * - Real-time updates via SSE
 * - Mark as read (individual and bulk)
 * - Preferences management
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'

// Module-level SSE token for reconnection after visibility change
let _sseToken: string | undefined

// Types
export interface Notification {
  id: number
  userId: string
  type: string
  title: string
  message: string | null
  studyId: string | null
  entityType: string | null
  entityId: string | null
  link: string | null
  priority: 'low' | 'normal' | 'high' | 'urgent'
  icon: string | null
  isRead: boolean
  readAt: string | null
  isArchived: boolean
  createdAt: string
  expiresAt: string | null
  timeAgo: string | null
}

export interface NotificationType {
  key: string
  name: string
  description: string | null
  category: 'data' | 'issues' | 'access' | 'security' | 'system' | 'exports'
  defaultInApp: boolean
  defaultEmail: 'immediate' | 'digest' | 'none'
  defaultPriority: 'low' | 'normal' | 'high' | 'urgent'
  icon: string | null
  isActive: boolean
}

export interface TypePreference {
  inApp: boolean
  email: 'immediate' | 'digest' | 'none'
}

export interface NotificationPreferences {
  userId: string
  inAppEnabled: boolean
  emailEnabled: boolean
  emailFrequency: 'immediate' | 'digest' | 'none'
  digestFrequency: 'daily' | 'weekly'
  digestTime: string
  digestTimezone: string
  quietHoursEnabled: boolean
  quietHoursStart: string | null
  quietHoursEnd: string | null
  slackEnabled: boolean
  slackUserId: string | null
  teamsEnabled: boolean
  teamsUserId: string | null
  typePreferences: Record<string, TypePreference>
}

interface NotificationState {
  // Data
  notifications: Notification[]
  unreadCount: number
  totalCount: number
  currentPage: number
  hasMore: boolean
  isLoading: boolean

  // Preferences
  preferences: NotificationPreferences | null
  notificationTypes: NotificationType[]
  isLoadingPreferences: boolean

  // UI state
  isDropdownOpen: boolean
  isPreferencesModalOpen: boolean

  // SSE state
  isConnected: boolean
  eventSource: EventSource | null

  // Actions
  fetchNotifications: (page?: number, reset?: boolean) => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markAsRead: (notificationId: number) => Promise<void>
  markAllAsRead: () => Promise<void>
  archiveNotification: (notificationId: number) => Promise<void>
  addNotification: (notification: Notification) => void

  // Preferences actions
  fetchPreferences: () => Promise<void>
  updatePreferences: (data: Partial<NotificationPreferences>) => Promise<void>
  fetchNotificationTypes: () => Promise<void>

  // UI actions
  setDropdownOpen: (open: boolean) => void
  setPreferencesModalOpen: (open: boolean) => void

  // SSE actions
  connectSSE: (token?: string) => void
  disconnectSSE: () => void

  // Legacy compatibility
  clearAll: () => void
  removeNotification: (id: number) => void
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      // Initial state
      notifications: [],
      unreadCount: 0,
      totalCount: 0,
      currentPage: 1,
      hasMore: false,
      isLoading: false,

      preferences: null,
      notificationTypes: [],
      isLoadingPreferences: false,

      isDropdownOpen: false,
      isPreferencesModalOpen: false,

      isConnected: false,
      eventSource: null,

      // Fetch notifications
      fetchNotifications: async (page = 1, reset = false) => {
        const state = get()
        if (state.isLoading) return

        set({ isLoading: true })

        try {
          const response = await api.get('/notifications', {
            params: {
              page,
              page_size: 20,
            },
          })

          const data = response.data

          set({
            notifications: reset
              ? data.notifications
              : [...state.notifications, ...data.notifications],
            totalCount: data.total,
            currentPage: page,
            hasMore: data.hasMore ?? false,
            unreadCount: data.unreadCount ?? 0,
            isLoading: false,
          })
        } catch (error) {
          console.error('Failed to fetch notifications:', error)
          set({ isLoading: false })
        }
      },

      // Fetch unread count only
      fetchUnreadCount: async () => {
        try {
          const response = await api.get('/notifications/unread-count')
          set({ unreadCount: response.data.count })
        } catch (error) {
          console.error('Failed to fetch unread count:', error)
        }
      },

      // Mark single notification as read
      markAsRead: async (notificationId: number) => {
        try {
          await api.patch(`/notifications/${notificationId}/read`)

          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.id === notificationId
                ? { ...n, isRead: true, readAt: new Date().toISOString() }
                : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          }))
        } catch (error) {
          console.error('Failed to mark notification as read:', error)
        }
      },

      // Mark all as read
      markAllAsRead: async () => {
        try {
          await api.post('/notifications/mark-read', { mark_all: true })

          set((state) => ({
            notifications: state.notifications.map((n) => ({
              ...n,
              isRead: true,
              readAt: new Date().toISOString(),
            })),
            unreadCount: 0,
          }))
        } catch (error) {
          console.error('Failed to mark all as read:', error)
        }
      },

      // Archive notification
      archiveNotification: async (notificationId: number) => {
        try {
          await api.delete(`/notifications/${notificationId}`)

          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== notificationId),
            totalCount: state.totalCount - 1,
          }))
        } catch (error) {
          console.error('Failed to archive notification:', error)
        }
      },

      // Add notification (from SSE)
      addNotification: (notification: Notification) => {
        set((state) => ({
          notifications: [notification, ...state.notifications].slice(0, 50),
          unreadCount: state.unreadCount + (notification.isRead ? 0 : 1),
          totalCount: state.totalCount + 1,
        }))
      },

      // Fetch preferences
      fetchPreferences: async () => {
        set({ isLoadingPreferences: true })

        try {
          const response = await api.get('/notifications/preferences')
          set({
            preferences: response.data,
            isLoadingPreferences: false,
          })
        } catch (error) {
          console.error('Failed to fetch preferences:', error)
          set({ isLoadingPreferences: false })
        }
      },

      // Update preferences
      updatePreferences: async (data: Partial<NotificationPreferences>) => {
        try {
          // Convert camelCase to snake_case for backend
          const typePrefs: Record<string, { in_app: boolean; email: string }> = {}
          if (data.typePreferences) {
            for (const [key, val] of Object.entries(data.typePreferences)) {
              typePrefs[key] = { in_app: val.inApp, email: val.email }
            }
          }

          const payload: Record<string, unknown> = {}
          if (data.inAppEnabled !== undefined) payload.in_app_enabled = data.inAppEnabled
          if (data.emailEnabled !== undefined) payload.email_enabled = data.emailEnabled
          if (data.emailFrequency !== undefined) payload.email_frequency = data.emailFrequency
          if (data.digestFrequency !== undefined) payload.digest_frequency = data.digestFrequency
          if (data.digestTime !== undefined) payload.digest_time = data.digestTime
          if (data.digestTimezone !== undefined) payload.digest_timezone = data.digestTimezone
          if (data.quietHoursEnabled !== undefined) payload.quiet_hours_enabled = data.quietHoursEnabled
          if (data.quietHoursStart !== undefined) payload.quiet_hours_start = data.quietHoursStart
          if (data.quietHoursEnd !== undefined) payload.quiet_hours_end = data.quietHoursEnd
          if (data.slackEnabled !== undefined) payload.slack_enabled = data.slackEnabled
          if (data.slackUserId !== undefined) payload.slack_user_id = data.slackUserId
          if (data.teamsEnabled !== undefined) payload.teams_enabled = data.teamsEnabled
          if (data.teamsUserId !== undefined) payload.teams_user_id = data.teamsUserId
          if (data.typePreferences) payload.type_preferences = typePrefs

          const response = await api.put('/notifications/preferences', payload)
          set({ preferences: response.data })
        } catch (error) {
          console.error('Failed to update preferences:', error)
          throw error
        }
      },

      // Fetch notification types
      fetchNotificationTypes: async () => {
        try {
          const response = await api.get('/notifications/preferences/types')
          set({ notificationTypes: response.data.types })
        } catch (error) {
          console.error('Failed to fetch notification types:', error)
        }
      },

      // UI actions
      setDropdownOpen: (open: boolean) => {
        set({ isDropdownOpen: open })

        // When opening dropdown, refresh notifications
        if (open) {
          get().fetchNotifications(1, true)
        }
      },

      setPreferencesModalOpen: (open: boolean) => {
        set({ isPreferencesModalOpen: open })

        if (open) {
          get().fetchPreferences()
          get().fetchNotificationTypes()
        }
      },

      // SSE connection with visibility handling and reconnect guard
      connectSSE: (token?: string) => {
        const state = get()

        // Don't connect if already connected
        if (state.eventSource) return

        // Don't connect if tab is hidden (saves a DB connection on the backend)
        if (typeof document !== 'undefined' && document.hidden) return

        // Store token for reconnection
        if (token) {
          _sseToken = token
        }
        const activeToken = token || _sseToken

        // Build URL with token as query parameter (EventSource doesn't support headers)
        let url = '/api/v1/notifications/stream'
        if (activeToken) {
          url += `?token=${encodeURIComponent(activeToken)}`
        }

        try {
          const eventSource = new EventSource(url, {
            withCredentials: true,
          })

          eventSource.addEventListener('notification', (event) => {
            try {
              const notification = JSON.parse(event.data) as Notification
              get().addNotification(notification)
            } catch (e) {
              console.error('Failed to parse notification event:', e)
            }
          })

          eventSource.addEventListener('heartbeat', () => {
            // Keep-alive received
          })

          eventSource.addEventListener('error', () => {
            get().disconnectSSE()

            // Fallback: fetch unread count once after 30s (no recurring poll)
            setTimeout(() => {
              if (!document.hidden) {
                get().fetchUnreadCount()
              }
            }, 30000)
          })

          eventSource.onopen = () => {
            set({ isConnected: true })
          }

          set({ eventSource, isConnected: true })
        } catch (error) {
          console.error('Failed to connect SSE:', error)
        }
      },

      disconnectSSE: () => {
        const state = get()

        if (state.eventSource) {
          state.eventSource.close()
          set({ eventSource: null, isConnected: false })
        }
      },

      // Legacy compatibility
      clearAll: () => set({ notifications: [], unreadCount: 0 }),
      removeNotification: (id: number) => get().archiveNotification(id),
    }),
    {
      name: 'sage-notifications',
      partialize: () => ({
        // Don't persist unreadCount - always fetch fresh from server on load
        // to avoid stale badge counts after page refresh
      }),
    }
  )
)

// Pause SSE when tab is hidden, reconnect when visible
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    const store = useNotificationStore.getState()
    if (document.hidden) {
      store.disconnectSSE()
    } else {
      // Tab became visible — reconnect SSE if not already connected
      if (!store.eventSource) {
        store.connectSSE()
        store.fetchUnreadCount()
      }
    }
  })
}

// Selectors
export const useUnreadCount = () =>
  useNotificationStore((state) => state.unreadCount)
export const useNotifications = () =>
  useNotificationStore((state) => state.notifications)

// Hook for initializing notifications on app load
export function useNotificationInit() {
  const fetchUnreadCount = useNotificationStore((state) => state.fetchUnreadCount)
  const connectSSE = useNotificationStore((state) => state.connectSSE)

  return {
    initialize: () => {
      fetchUnreadCount()
      connectSSE()
    },
  }
}
